import { FC } from 'react';
import { Form } from 'formik';
import { Modal as QcsModal } from '@mui/material';
import { QcsModalBox } from '../../common/basic/QcsModalBox';
import { SectionHeader } from '../../common/SectionHeader';
import { FormikDebounce } from '../../common/form/FormikDebounce';
import { FormContainer } from '../../common/form/FormContainer';
import { SupplierEmployeeAutocomplete } from '../../common/form/SupplierEmployeeAutocomplete';
import { SubmitButton } from '../../common/form/SubmitButton';
import { useInvitation } from './Tabs/invitationFunctions';
import { useAppSelector } from '../../../store';
import { selectIdentity } from '../../../store/entities/identity';
import { getFirstLastNameObj } from '../../../utils/format';
import { UserRole } from '@api/models/enums/UserRole';
import { UserState } from '@api/models/enums/UserState';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { ConfirmInvitationRequest } from '@api/models/invitation/ConfirmInvitationRequest';
import * as Yup from 'yup';
import { validations } from '../../../utils/validations';
import { invitationService } from '@api/services/services';
import { ShowError } from '../../ShowError';
import { resources } from '@api/resources';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  responsiblePerson: ReferenceDto | null;
}

export const ConfirmResponsiblePersonModal: FC<Props> = ({ open, onClose }) => {
  const { t, invitation, callWithLock, reloadInvitation } = useInvitation();
  const identity = useAppSelector(selectIdentity);

  const confirmDetails = async (responsiblePerson: ReferenceDto) => {
    await callWithLock(async () => {
      const data: ConfirmInvitationRequest = {
        responsiblePerson: {
          id: responsiblePerson.id,
          name: responsiblePerson.name ?? getFirstLastNameObj(responsiblePerson),
        },
      };

      await invitationService.confirmInvitationDetails(invitation.id!, data);
      await reloadInvitation();
      onClose();
    });
  };

  const handleSubmit = async (values: FormValues) => {
    await confirmDetails(values.responsiblePerson!);
  };

  const responsiblePersonRef: ReferenceDto | null = invitation.responsiblePerson
    ? {
        id: invitation.responsiblePerson.id!,
        name: getFirstLastNameObj(invitation.responsiblePerson),
      }
    : null;

  return (
    <QcsModal open={open} onClose={onClose}>
      <QcsModalBox>
        <SectionHeader title={t('invitation.responsiblePerson')} />
        <FormikDebounce<FormValues>
          initialValues={{
            responsiblePerson: responsiblePersonRef,
          }}
          validationSchema={Yup.object({
            responsiblePerson: validations.objectRequired(t),
          })}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <Form>
              <FormContainer>
                <SupplierEmployeeAutocomplete
                  name="responsiblePerson"
                  label={t('invitation.responsibleSupplierPerson')}
                  required
                  supplierId={invitation.supplier?.id}
                  role={UserRole.ExternalManager}
                  state={[UserState.Active, UserState.Invited]}
                />
                {values.responsiblePerson?.id === identity.id ? (
                  <SubmitButton text="invitation.confirmSelfAsResponsiblePerson" />
                ) : (
                  <SubmitButton text="common.save" />
                )}
              </FormContainer>
            </Form>
          )}
        </FormikDebounce>
        <ShowError resource={resources.confirmInvitationDetails} />
      </QcsModalBox>
    </QcsModal>
  );
};
